import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import Uppy from '@uppy/core';
import { Dashboard } from '@uppy/react';
import ImageEditor from '@uppy/image-editor';
import Webcam from '@uppy/webcam';
import XHRUpload from '@uppy/xhr-upload';

import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/webcam/css/style.min.css';
import { formatFileSize } from '../../util/formatFileSize';
import { API_BASE_URL } from '../../config/apiConfig';
import {
  fetchStagingUploadStatus,
  requestStagingUploadInit,
  finalizeStagingUpload,
} from '../../services/stagingUploadService';
import { normalizeOriginalForUpload } from '../../services/imageNormalizeService';
import { PIN_FAILED_MESSAGE, readFileBytesWithRetry } from '../../util/fileBytes';
import { withRetries } from '../../util/retry';
import { describeFailedUploads } from '../../util/uploadFailureCopy';
import { ImageOff, RotateCw } from 'lucide-react';
import Popup from '../ui/Popup';
import Button from '../ui/Button';
import { resolveMaxFileSize } from '../../util/uploadRestrictions';
import { setUploadsInFlight } from '../../util/saveTracker';

const CLOUD_NAME = process.env.REACT_APP_CLOUD_NAME;
const UPLOAD_PRESET = 'unsigned_uploads';

const BATCH_UPLOAD_DELAY_MS = 200;
/** Attempts for the browser -> S3 PUT. A connection blip mid-transfer used to fail the photo outright. */
const PUT_ATTEMPTS = 3;
const STAGING_UPLOAD_CONCURRENCY = 4;
const DESKTOP_DASHBOARD_HEIGHT = 180;
const MOBILE_DASHBOARD_HEIGHT = 220;
const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

function parseJsonUploadResponse(xhr) {
  try {
    if (xhr == null) return {};

    if (typeof xhr === 'string') {
      return JSON.parse(xhr || '{}');
    }

    if (typeof xhr.response === 'object' && xhr.response !== null) {
      return xhr.response;
    }

    return JSON.parse(xhr.responseText || '{}');
  } catch (err) {
    console.error('Failed to parse upload response', err);
    return {};
  }
}

function parseCloudinaryUploadResponse(xhr) {
  return parseJsonUploadResponse(xhr);
}

function parseStagingUploadResponse(xhr) {
  const parsed = parseJsonUploadResponse(xhr);
  const data = parsed?.data || parsed;
  return {
    ...data,
    mediaId: data.mediaId || null,
    stagingKey: data.stagingKey,
    previewUrl: data.previewUrl,
    secure_url: data.previewUrl,
    url: data.previewUrl,
    public_id: data.stagingKey,
    original_filename: data.original_filename,
  };
}

function mapUploadedFile(item) {
  const file = item;
  const res = item.response?.body || {};
  const imageUrl = res.previewUrl || res.secure_url || res.url || file.preview;
  const fileBlob = file.data;
  let blobUrl = null;

  if (fileBlob instanceof Blob) {
    try {
      blobUrl = URL.createObjectURL(fileBlob);
    } catch (err) {
      console.error('Failed to create blob URL:', err);
    }
  }

  return {
    file: fileBlob,
    blobUrl,
    url: imageUrl,
    publicId: res.stagingKey || res.public_id,
    stagingKey: res.stagingKey || null,
    mediaId: res.mediaId || null,
    filename: res.original_filename || file.name,
    size: formatFileSize(file.size),
    filetype:
      res.format?.toUpperCase() ||
      file.type?.split('/')[1]?.toUpperCase(),
    caption: '',
  };
}

/**
 * Wait until multi-select finishes adding files before uploading.
 * autoProceed fires before sibling files from the picker are all queued.
 */
function scheduleBatchUpload(uppyInstance) {
  let batchTimer = null;

  const flushBatch = () => {
    batchTimer = null;
    const pending = uppyInstance
      .getFiles()
      .filter((file) => !file.progress.uploadStarted && !file.error);

    if (pending.length === 0) {
      return;
    }

    uppyInstance.upload().catch((err) => {
      if (!err?.isRestriction) {
        console.error('Upload batch failed:', err);
      }
    });
  };

  uppyInstance.on('files-added', () => {
    if (batchTimer) {
      clearTimeout(batchTimer);
    }
    batchTimer = setTimeout(flushBatch, BATCH_UPLOAD_DELAY_MS);
  });
}

function clearSuccessfulUploads(uppyInstance, successfulFiles) {
  successfulFiles.forEach((file) => {
    try {
      uppyInstance.removeFile(file.id);
    } catch (err) {
      console.warn('Could not remove uploaded file from uploader:', file.id, err);
    }
  });
}

function useDashboardHeight() {
  const [height, setHeight] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches
      ? MOBILE_DASHBOARD_HEIGHT
      : DESKTOP_DASHBOARD_HEIGHT,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const syncHeight = () => {
      setHeight(mediaQuery.matches ? MOBILE_DASHBOARD_HEIGHT : DESKTOP_DASHBOARD_HEIGHT);
    };

    syncHeight();
    mediaQuery.addEventListener('change', syncHeight);
    return () => mediaQuery.removeEventListener('change', syncHeight);
  }, []);

  return height;
}

/**
 * PUT a file to a presigned S3 URL with upload progress callbacks.
 */
function putFileWithProgress(url, file, headers, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    Object.entries(headers || {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      if (xhr.status === 403) {
        reject(
          Object.assign(new Error('The upload for this photo timed out. Please try again.'), {
            status: 403,
          }),
        );
        return;
      }
      reject(
        Object.assign(
          new Error(`We couldn't save this photo. Please try again. (Error ${xhr.status})`),
          { status: xhr.status },
        ),
      );
    };
    xhr.onerror = () =>
      reject(
        Object.assign(
          new Error(
            'We lost the connection while saving this photo. Please check your internet and try again.',
          ),
          { status: 0 },
        ),
      );
    xhr.send(file);
  });
}

/**
 * Copy a picker file's bytes into memory the moment it is added.
 *
 * Chrome on Android hands us File objects backed by photo-picker content URIs
 * whose read grant can lapse a few seconds later — reads then fail with
 * NotReadableError, and the raw-file PUT dies as a "network error".
 * Reproduced 2026-09-10 on a Pixel 9 emulator: reads right after the picker
 * closes succeed; the same File can be unreadable ~10s later, and Retry on the
 * dead handle fails identically (the customer's two-strike pattern). Reading
 * once, early, and sequentially, then replacing `file.data` with an in-memory
 * File means nothing downstream ever touches the OS handle again.
 */
function registerFileBytePinning(uppyInstance) {
  const pins = new Map();
  let chain = Promise.resolve();

  uppyInstance.on('file-added', (file) => {
    const data = file?.data;
    if (!(data instanceof Blob)) return;
    // One read at a time: cheap, and it keeps the picker grants from being
    // hammered concurrently while they are still fresh.
    const pin = chain
      .catch(() => {})
      .then(async () => {
        const buffer = await readFileBytesWithRetry(data);
        const pinned = new File([buffer], data.name || file.name || 'photo', {
          type: data.type || file.type || '',
          lastModified: data.lastModified || Date.now(),
        });
        if (uppyInstance.getFile(file.id)) {
          uppyInstance.setFileState(file.id, { data: pinned });
        }
        return pinned;
      });
    pin.catch((err) => {
      // eslint-disable-next-line no-console
      console.warn(`[STAGING-PIN] could not read ${file.name}: ${err?.name || ''} ${err?.message || err}`);
    });
    chain = pin;
    pins.set(file.id, pin);
  });

  uppyInstance.on('file-removed', (file) => {
    pins.delete(file?.id);
  });

  return (fileId) => pins.get(fileId);
}

/**
 * Presigned direct-to-S3 staging uploader. Uppy's post-processor hook does not
 * run after a successful S3 PUT (uploadComplete is already true), so we use a
 * custom uploader that runs init -> PUT -> finalize in one awaited step.
 */
function registerDirectStagingUploader(uppyInstance, { bookId, isCover, useBatchScheduling }) {
  const limit = useBatchScheduling ? STAGING_UPLOAD_CONCURRENCY : 1;
  const getPin = registerFileBytePinning(uppyInstance);

  const uploadOneStagingFile = async (file) => {
    const meta = file.meta || {};
    const resolvedBookId = meta.bookId || bookId;
    const variant = meta.variant || 'original';
    const resolvedIsCover =
      meta.isCover === 'true' || meta.isCover === true || isCover;

    const emitProgress = (bytesUploaded, bytesTotal) => {
      const current = uppyInstance.getFile(file.id) || file;
      uppyInstance.emit('upload-progress', current, {
        uploadStarted: current.progress?.uploadStarted ?? Date.now(),
        bytesUploaded,
        bytesTotal,
      });
    };

    const bytesTotal = file.size || 1;
    emitProgress(0, bytesTotal);

    // Wait for the in-memory copy (see registerFileBytePinning). If the OS
    // handle was already dead, fail here with an actionable message instead
    // of a "network error" from a doomed PUT.
    const pin = getPin(file.id);
    if (pin) {
      try {
        await pin;
      } catch (err) {
        const failure = new Error(PIN_FAILED_MESSAGE);
        failure.cause = err;
        throw failure;
      }
    }
    const pinnedFile = uppyInstance.getFile(file.id)?.data;
    if (pinnedFile instanceof Blob) {
      file = { ...file, data: pinnedFile };
    }

    // Variant A: normalize originals in the browser (Worker: EXIF-upright,
    // long-edge cap, JPEG) so the wire carries a small JPEG and the API can
    // finalize metadata-only. Crops are already browser-produced JPEGs.
    // On normalize failure we upload the raw file (server Sharp fallback).
    let uploadFile = file.data;
    let clientNormalized = false;
    let normalizeTimings = {};
    if (variant === 'original') {
      const normalized = await normalizeOriginalForUpload(file.data);
      uploadFile = normalized.file;
      clientNormalized = normalized.clientNormalized;
      normalizeTimings = {
        normalize_start: normalized.normalize_start,
        normalize_end: normalized.normalize_end,
        normalized_bytes: normalized.normalized_bytes,
      };
      if (clientNormalized && uploadFile !== file.data) {
        // Keep the normalized JPEG as the file the App holds on to. Everything
        // downstream (grid thumbnail, auto-crop, crop popups) builds blob URLs
        // from `file.data`, and the browser cannot decode the raw original when
        // it is HEIC (Chrome has no HEIF decoder) or too large for the device.
        // The worker/heic2any path above already decoded it once — reuse that.
        uppyInstance.setFileState(file.id, { data: uploadFile });
        // Give the card in the upload box a real thumbnail. Uppy generated its
        // preview from the raw file at add time, which fails for HEIC on Chrome.
        const current = uppyInstance.getFile(file.id);
        if (current && !current.preview) {
          uppyInstance.setFileState(file.id, { preview: URL.createObjectURL(uploadFile) });
        }
      }
      emitProgress(Math.round(0.05 * bytesTotal), bytesTotal);
    }

    // Per-file phase timings. put_start/put_end are wall-clock ms so the
    // batch PUT phase is measurable as overlap (earliest put_start → latest
    // put_end), not the sum of per-file put_ms.
    const initStart = Date.now();
    const requestInit = () =>
      requestStagingUploadInit({
        bookId: resolvedBookId,
        variant,
        isCover: resolvedIsCover,
        mediaId: meta.mediaId || null,
        contentLength: uploadFile.size,
        filename: file.name,
      });
    let init = await requestInit();
    const initMs = Date.now() - initStart;
    uppyInstance.setFileMeta(file.id, { stagingKey: init.stagingKey });

    // The bytes are in memory (pinned + normalized), so a failed PUT is safe
    // to repeat. Network drops and 5xx reuse the same presigned URL; a 403
    // means the link was rejected or expired, so mint a fresh one first.
    // 4xx other than 403 are not retried — the request itself is wrong.
    const putStart = Date.now();
    let putAttempts = 0;
    await withRetries(
      (attempt) => {
        putAttempts = attempt;
        return putFileWithProgress(init.uploadUrl, uploadFile, init.headers, (loaded, putTotal) => {
          const scaled = Math.round((0.05 + (loaded / putTotal) * 0.75) * bytesTotal);
          emitProgress(scaled, bytesTotal);
        });
      },
      {
        attempts: PUT_ATTEMPTS,
        shouldRetry: (err) => err?.status === 0 || err?.status === 403 || err?.status >= 500,
        onRetry: async (err, attempt) => {
          // eslint-disable-next-line no-console
          console.warn(
            `[STAGING-PUT] attempt ${attempt} failed for ${file.name} (status ${err?.status}): ${err?.message}; retrying`,
          );
          if (err?.status === 403) {
            init = await requestInit();
            uppyInstance.setFileMeta(file.id, { stagingKey: init.stagingKey });
          }
        },
      },
    );
    const putEnd = Date.now();

    emitProgress(Math.round(0.85 * bytesTotal), bytesTotal);

    const clientTimings = {
      init_ms: initMs,
      put_ms: putEnd - putStart,
      put_start: putStart,
      put_end: putEnd,
      put_attempts: putAttempts,
      bytes: file.size,
      ...normalizeTimings,
    };
    // eslint-disable-next-line no-console
    console.log(
      `[STAGING-TIMING] ${JSON.stringify({
        stagingKey: init.stagingKey,
        variant,
        isCover: resolvedIsCover,
        filename: file.name,
        clientNormalized,
        ...clientTimings,
      })}`,
    );

    const result = await finalizeStagingUpload({
      bookId: resolvedBookId,
      stagingKey: init.stagingKey,
      variant,
      isCover: resolvedIsCover,
      mediaId: meta.mediaId || null,
      filename: file.name,
      clientTimings,
      clientNormalized,
    });

    emitProgress(bytesTotal, bytesTotal);

    const current = uppyInstance.getFile(file.id) || file;
    uppyInstance.emit('upload-success', current, {
      status: 200,
      body: result,
      uploadURL: result.url,
    });
  };

  uppyInstance.addUploader(async (fileIDs) => {
    const files = fileIDs
      .map((fileID) => uppyInstance.getFile(fileID))
      .filter(Boolean);

    if (files.length === 0) {
      return;
    }

    uppyInstance.emit('upload-start', files);

    let cursor = 0;
    const worker = async () => {
      while (cursor < files.length) {
        const file = files[cursor];
        cursor += 1;
        try {
          await uploadOneStagingFile(file);
        } catch (err) {
          const current = uppyInstance.getFile(file.id) || file;
          uppyInstance.emit('upload-error', current, err);
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(limit, files.length) }, worker),
    );
  });
}

function createUppyInstance({
  single,
  accept,
  useStaging,
  useDirect,
  bookId,
  isCover = false,
}) {
  // Debounce multi-file uploads on BOTH paths (staging and Cloudinary). Without
  // this the Cloudinary path runs on autoProceed, which fires before the OS
  // picker finishes queueing sibling files: the first file uploads, completes,
  // and the cancelAll() in handleComplete aborts the rest — so only 1 of N
  // selected photos lands on the first attempt. See scheduleBatchUpload.
  const useBatchScheduling = !single;

  const uppyInstance = new Uppy({
    id: useStaging
      ? `file-uploader-staging-${bookId || 'none'}`
      : 'file-uploader',
    restrictions: {
      maxNumberOfFiles: single ? 1 : null,
      // Only the direct-staging uploader normalizes before the PUT.
      maxFileSize: resolveMaxFileSize({ useStaging, useDirect }),
      allowedFileTypes:
        accept === 'image/*'
          ? ['image/*', '.jpg', '.jpeg', '.png', '.webp', '.bmp', '.gif', '.heic', '.heif']
          : ['image/*', '.pdf', '.jpg', '.jpeg', '.png'],
    },
    autoProceed: !useBatchScheduling,
  });

  uppyInstance.use(ImageEditor, {
    id: 'ImageEditor',
    cropperOptions: {
      aspectRatio: undefined,
      viewMode: 1,
    },
    actions: {
      revert: true,
      rotate: true,
      granularRotate: true,
      flip: true,
      zoomIn: true,
      zoomOut: true,
      cropSquare: true,
      cropWidescreen: true,
      cropWidescreenVertical: true,
    },
  });

  uppyInstance.use(Webcam, { id: 'Webcam' });

  if (useStaging && bookId) {
    if (useDirect) {
      registerDirectStagingUploader(uppyInstance, { bookId, isCover, useBatchScheduling });
    } else {
      uppyInstance.use(XHRUpload, {
        id: 'XHRUpload',
        endpoint: `${API_BASE_URL}media/upload-staging`,
        formData: true,
        fieldName: 'file',
        bundle: false,
        limit: useBatchScheduling ? STAGING_UPLOAD_CONCURRENCY : undefined,
        allowedMetaFields: ['bookId', 'variant', 'isCover'],
        headers: () => ({
          Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
        }),
        getResponseData: parseStagingUploadResponse,
      });
    }

    uppyInstance.setMeta({
      bookId: String(bookId),
      variant: 'original',
      ...(isCover ? { isCover: 'true' } : {}),
    });
  } else {
    uppyInstance.use(XHRUpload, {
      id: 'XHRUpload',
      endpoint: `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
      formData: true,
      fieldName: 'file',
      bundle: false,
      allowedMetaFields: ['upload_preset'],
      getResponseData: parseCloudinaryUploadResponse,
    });

    uppyInstance.setMeta({ upload_preset: UPLOAD_PRESET });
  }

  if (useBatchScheduling) {
    scheduleBatchUpload(uppyInstance);
  }

  return uppyInstance;
}

/**
 * `variant="picker"` hides the Uppy box and drives it through a ref instead:
 * `openPicker()` opens the device's file picker, `addFiles(fileList)` queues
 * dropped files. Uploads, retries and HEIC handling stay exactly as in the
 * dashboard variant; failures show in the redesign's pop-up ("N photos
 * didn't upload", Try again / Skip) instead of Uppy's cards.
 *
 * `onBusy(count)` says when photos are on their way: the number picked as soon
 * as they are added, 0 once the batch is done (after `onChange`), so a page can
 * show that something is happening while they upload.
 */
const FileUploader = forwardRef(function FileUploader({
  onChange = () => {},
  single = false,
  accept = 'image/*',
  onRemove = () => {},
  bookId = null,
  isCover = false,
  variant = 'dashboard',
  onBusy = () => {},
}, ref) {
  const [, setIsUploading] = useState(false);
  // The Uppy listeners are bound once; read the latest callbacks through refs.
  // (A stale onChange kept the page's state from when the uploader mounted, so a
  // new cover could land behind an old one, or another book's.)
  const onBusyRef = useRef(onBusy);
  onBusyRef.current = onBusy;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [, setUploadedFiles] = useState([]);
  // One persistent line per failed photo, rendered under the box (the widget's
  // own fading bubbles are disabled). Keyed by Uppy file id.
  const [failedUploads, setFailedUploads] = useState([]);
  const [notice, setNotice] = useState(null);
  // Position of each file within the batch the customer picked, so a failure
  // can be named ("Photo 14 of 20") even when the file itself is unreadable.
  const batchPositionsRef = useRef(new Map());
  const dashboardHeight = useDashboardHeight();
  // Uppy lays file cards out as 71px rows up to 576px wide and 200px grid
  // tiles above that; size the box for one failed card accordingly.
  const wrapRef = useRef(null);
  const [wideBox, setWideBox] = useState(false);
  const [stagingEnabled, setStagingEnabled] = useState(false);
  const [directEnabled, setDirectEnabled] = useState(false);
  const [stagingReady, setStagingReady] = useState(!bookId);
  const [uppy, setUppy] = useState(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const check = () => setWideBox(el.getBoundingClientRect().width > 576);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [uppy]);
  const useBatchScheduling = !single;

  useEffect(() => {
    let cancelled = false;

    if (!bookId) {
      setStagingEnabled(false);
      setDirectEnabled(false);
      setStagingReady(true);
      return undefined;
    }

    setStagingReady(false);

    fetchStagingUploadStatus()
      .then((status) => {
        if (!cancelled) {
          const enabled = !!(status.enabled && status.configured);
          setStagingEnabled(enabled);
          setDirectEnabled(enabled && !!status.directUpload);
          setStagingReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStagingEnabled(false);
          setDirectEnabled(false);
          setStagingReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bookId]);

  useEffect(() => {
    if (!stagingReady) return undefined;

    const instance = createUppyInstance({
      single,
      accept,
      useStaging: stagingEnabled && !!bookId,
      useDirect: directEnabled && !!bookId,
      bookId,
      isCover,
    });
    setUppy(instance);

    return () => {
      instance.destroy();
      setUppy(null);
    };
  }, [stagingReady, stagingEnabled, directEnabled, single, accept, bookId, isCover]);

  useEffect(() => {
    if (!uppy) return undefined;

    const handleSuccess = async (file, response) => {
      try {
        const fileData = mapUploadedFile({ ...file, response });

        if (single) {
          setUploadedFiles([fileData]);
        } else {
          setUploadedFiles((prev) => [...prev, fileData]);
        }
      } catch (err) {
        console.error(' Error handling upload success:', err);
      }
    };

    const forgetFailure = (file) => {
      const id = file?.id;
      if (!id) return;
      setFailedUploads((prev) => prev.filter((f) => f.id !== id));
    };

    const handleFilesAdded = (files) => {
      const total = files.length;
      files.forEach((file, index) => {
        batchPositionsRef.current.set(file.id, { position: index + 1, total });
      });
      setNotice(null);
      onBusyRef.current(total);
      setUploadsInFlight(total);
    };

    const handleError = (file, error) => {
      console.error(' Upload error:', file?.name, error);
      setIsUploading(false);
      if (!file?.id) return;
      const where = batchPositionsRef.current.get(file.id) || {};
      setFailedUploads((prev) => [
        ...prev.filter((f) => f.id !== file.id),
        { id: file.id, error, position: where.position, total: where.total },
      ]);
    };

    const handleComplete = async (result) => {
      setIsUploading(false);

      const successful = result.successful || [];

      if (successful.length > 0) {
        const fileDataArray = successful
          .map(mapUploadedFile)
          .filter((item) => item.url && item.publicId);

        if (fileDataArray.length === 0) {
          setNotice('Your photo uploaded, but something went wrong showing it. Please try again.');
        } else {
          onChangeRef.current(fileDataArray);

          if (useBatchScheduling) {
            clearSuccessfulUploads(uppy, successful);
          } else {
            setTimeout(() => {
              uppy.cancelAll();
            }, 100);
          }
        }
      }
      onBusyRef.current(0);
      setUploadsInFlight(0);
    };

    const handleRetry = (fileId) => {
      setFailedUploads((prev) => prev.filter((f) => f.id !== fileId));
    };
    const handleRetryAll = () => setFailedUploads([]);
    const handleCancelAll = () => {
      setFailedUploads([]);
      batchPositionsRef.current.clear();
      onBusyRef.current(0);
      setUploadsInFlight(0);
    };

    uppy.on('upload', () => setIsUploading(true));
    uppy.on('files-added', handleFilesAdded);
    uppy.on('upload-success', handleSuccess);
    uppy.on('upload-success', forgetFailure);
    uppy.on('upload-error', handleError);
    uppy.on('file-removed', forgetFailure);
    uppy.on('upload-retry', handleRetry);
    uppy.on('retry-all', handleRetryAll);
    uppy.on('cancel-all', handleCancelAll);
    uppy.on('complete', handleComplete);

    return () => {
      uppy.off('files-added', handleFilesAdded);
      uppy.off('upload-success', handleSuccess);
      uppy.off('upload-success', forgetFailure);
      uppy.off('upload-error', handleError);
      uppy.off('file-removed', forgetFailure);
      uppy.off('upload-retry', handleRetry);
      uppy.off('retry-all', handleRetryAll);
      uppy.off('cancel-all', handleCancelAll);
      uppy.off('complete', handleComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uppy, single, useBatchScheduling]);

  useEffect(() => {
    return () => {
      uppy?.getFiles().forEach((file) => {
        if (file.preview && file.preview.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [uppy]);

  useImperativeHandle(
    ref,
    () => ({
      /** Open the device's file picker (the same input Uppy's "browse" uses). */
      openPicker: () => {
        const input = wrapRef.current?.querySelector('.uppy-Dashboard-input');
        if (input) input.click();
      },
      /** Queue dropped or picked files; restrictions (type, size) are reported like any other failure. */
      addFiles: (fileList) => {
        if (!uppy || !fileList) return;
        const files = Array.from(fileList).map((file) => ({ name: file.name, type: file.type, data: file, source: 'Local' }));
        try {
          uppy.addFiles(files);
        } catch (err) {
          if (!err?.isRestriction) console.error('Could not add files:', err);
        }
      },
      isReady: () => !!uppy,
      /** The file types the picker offers, for a page's own file input (the same list Uppy's input uses). */
      pickerAccept: () => wrapRef.current?.querySelector('.uppy-Dashboard-input')?.accept || accept,
    }),
    [uppy],
  );

  const skipFailed = () => {
    failedUploads.forEach((f) => {
      try {
        uppy?.removeFile(f.id);
      } catch (_) {
        /* already gone */
      }
    });
    setFailedUploads([]);
    setNotice(null);
  };
  const retryFailed = () => {
    setNotice(null);
    uppy?.retryAll().catch((err) => console.error('Retry failed:', err));
  };

  if (!stagingReady || !uppy) {
    if (variant === 'picker') return null;
    return (
      <div className="text-sm text-gray-600 py-6 text-center">
        Preparing uploader…
      </div>
    );
  }

  if (variant === 'picker') {
    const failedCount = failedUploads.length;
    const lines = describeFailedUploads(failedUploads, { single });
    return (
      <div ref={wrapRef} className="md-uploader md-uploader--picker">
        {/* Kept in the DOM, out of view, so its file input and upload pipeline keep working.
            No thumbnails: nobody sees this box, and decoding every raw photo for one cost phones main-thread time. */}
        <div aria-hidden="true" className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0">
          <Dashboard uppy={uppy} height={120} width={320} showProgressDetails={false} disableInformer disableThumbnailGenerator proudlyDisplayPoweredByUppy={false} theme="light" />
        </div>
        <Popup
          open={failedCount > 0 || !!notice}
          onClose={skipFailed}
          alert
          tone="red"
          icon={<ImageOff className="h-7 w-7" aria-hidden="true" />}
          title={
            failedCount > 1
              ? `${failedCount} photos didn't upload`
              : single
                ? "Your cover photo didn't upload"
                : "A photo didn't upload"
          }
          actions={[
            ...(failedCount
              ? [
                  <Button key="retry" onClick={retryFailed} icon={<RotateCw className="h-4 w-4" aria-hidden="true" />}>
                    Try again
                  </Button>,
                ]
              : []),
            <Button key="skip" variant="secondary" onClick={skipFailed}>
              {failedCount > 1 ? `Skip these ${failedCount}` : failedCount === 1 ? 'Skip it' : 'OK'}
            </Button>,
          ]}
        >
          {lines.map((line) => (
            <p key={line} className="m-0">
              {line}
            </p>
          ))}
          {notice ? <p className="m-0">{notice}</p> : null}
        </Popup>
      </div>
    );
  }

  // The box needs room for the failed photo's card (header + card + status bar).
  const hasFailures = failedUploads.length > 0;
  // Same-cause failures collapse into one line ("Photos 3, 7 and 12 of 20…").
  const failureLines = describeFailedUploads(failedUploads, { single });
  // Header (40) + 71px per card row (up to 3, then the list scrolls) + status bar (50).
  // Above 576px Uppy uses a 200px grid row that holds several cards.
  const failureBoxHeight = wideBox ? 300 : 90 + 71 * Math.min(failedUploads.length, 3);
  const boxHeight = hasFailures ? Math.max(dashboardHeight, failureBoxHeight) : dashboardHeight;

  return (
    <div
      ref={wrapRef}
      className={hasFailures ? 'md-uploader md-uploader--has-failures' : 'md-uploader'}
    >
      {(hasFailures || notice) && (
        <div
          role="alert"
          data-testid="upload-failure-message"
          className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm md:text-base text-red-900 space-y-1"
        >
          {failureLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
          {notice && <p>{notice}</p>}
        </div>
      )}
      <Dashboard
        uppy={uppy}
        height={boxHeight}
        width="100%"
        showProgressDetails={false}
        disableInformer
        note={`Drag and drop or click to browse • Supports: ${
          accept === 'image/*'
            ? 'JPG, JPEG, PNG, WEBP, BMP, GIF, HEIC'
            : 'JPG, JPEG, PNG, PDF'
        } • Max file size: 10MB`}
        proudlyDisplayPoweredByUppy={false}
        showLinkToFileUploadResult={false}
        showRemoveButtonAfterComplete={false}
        theme="light"
        plugins={['ImageEditor', 'Webcam']}
        locale={{
          strings: {
            uploadFailed:
              failedUploads.length > 1
                ? `${failedUploads.length} photos didn't upload`
                : single
                  ? "Your cover photo didn't upload"
                  : "A photo didn't upload",
            filesUploadedOfTotal: { 0: '', 1: '' },
            uploadingXFiles: { 0: 'Sending %{smart_count} photo', 1: 'Sending %{smart_count} photos' },
            xFilesSelected: { 0: '%{smart_count} photo ready', 1: '%{smart_count} photos ready' },
            processingXFiles: { 0: 'Preparing %{smart_count} photo', 1: 'Preparing %{smart_count} photos' },
            retry: 'Retry',
            retryUpload: 'Try this photo again',
            removeFile: 'Remove this photo',
          },
        }}
      />
    </div>
  );
});

export default FileUploader;
